import { apiClient } from './index.js';
import { sanitizePostData } from '@/utils/index.js';
import { getBlogPosts } from '@/api/blog.js'

async function getEvent({ eventId }) {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetEvent',
    params: { EventId: eventId },
  });
  return data;
}

async function searchParticipant(searchFormData) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/SearchParticipant',
    data: sanitizePostData(searchFormData),
    generalErrorsOnly: true,
  });
  return data;
}

async function getAchievementFunctionDescriptions() {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetAchievementFunctionDescriptions',
  });
  return data;
}

async function getEventParticipants({ eventId }) {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetEventParticipants',
    params: { EventId: eventId },
  });
  return data;
}

async function getIncomingEventApplications({ eventId }) {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetIncomingEventApplications',
    params: { EventId: eventId },
  });
  return data;
}

async function getOutgoingInvitations({ eventId }) {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetOutgoingInvitations',
    params: { EventId: eventId },
  });
  return data;
}

async function getEventPosts({ eventId, skip, limit }) {
  return getBlogPosts( 'Event', eventId, 'Public', null, null, skip, limit);
}

async function getActivityEventTypeDescriptions({ activityId }) {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetActivityEventTypeDescriptions',
    params: { ActivityId: activityId },
  });
  return data;
}

async function getEventLevelTypeDescriptions({ activityId }) {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetEventLevelTypeDescriptions',
    params: { ActivityId: activityId },
  });
  return data;
}

async function getEventRoleDescriptions({ eventId }) {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetEventRoleDescriptions',
    params: { EventId: eventId },
  });
  return data;
}

async function getEventRoleForApplicationDescriptions({ applicationId }) {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetEventRoleForApplicationDescriptions',
    params: { ApplicationId: applicationId },
  });
  return data;
}

async function getEventRoleForParticipantDescriptions({ participantId }) {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetEventRoleForParticipantDescriptions',
    params: { ParticipantId: participantId },
  });
  return data;
}

async function addEvent(eventFormData) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/AddEvent',
    data: sanitizePostData(eventFormData),
    generalErrorsOnly: true,
  });
  return data;
}

async function updateEvent({
  link,
  name,
  startDate,
  endDate,
  details,
  location,
  maxParticipants,
  isPrivate,
  eventId,
  eventLevelTypeId,
}) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/UpdateEvent',
    data: sanitizePostData({
      link,
      name,
      startDate,
      endDate,
      details,
      location,
      maxParticipants,
      isPrivate,
      eventId,
      eventLevelTypeId,
    }),
    generalErrorsOnly: true,
  });
  return data;
}

async function publishEvent({ eventId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/PublishEvent',
    data: sanitizePostData({
      eventId,
    }),
  });
  return data;
}

async function cancelEvent({ eventId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/CancelEvent',
    data: sanitizePostData({
      eventId,
    }),
  });
  return data;
}

async function deleteEvent({ eventId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/DeleteEvent',
    data: sanitizePostData({
      eventId,
    }),
  });
  return data;
}

async function inviteUsersToEvent(formData) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/InviteUsersToEvent',
    data: sanitizePostData(formData),
    generalErrorsOnly: true,
  });
  return data;
}

async function sendApplicationToEvent({ eventId, comments }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/SendApplicationToEvent',
    data: sanitizePostData({
      eventId,
      comments,
    }),
    generalErrorsOnly: true,
  });
  return data;
}

async function changeParticipantRole({ participantId, eventRoleId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/ChangeParticipantRole',
    data: sanitizePostData({
      participantId,
      eventRoleId,
    }),
  });
  return data;
}

async function approveEventApplication({ applicationId, eventRoleId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/ApproveEventApplication',
    data: sanitizePostData({
      applicationId,
      eventRoleId,
    }),
  });
  return data;
}

async function leaveEventApplicationOnHold({ applicationId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/LeaveEventApplicationOnHold',
    data: sanitizePostData({ applicationId }),
  });
  return data;
}

async function putEventApplicationToWaitList({ applicationId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/PutEventApplicationToWaitList',
    data: sanitizePostData({ applicationId }),
  });
  return data;
}

async function rejectEventApplication({ applicationId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/RejectedEventApplication',
    data: sanitizePostData({ applicationId }),
  });
  return data;
}

async function returnEventApplication({ applicationId, responseComments }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/ReturnEventApplication',
    data: sanitizePostData({ applicationId, responseComments }),
    generalErrorsOnly: true,
  });
  return data;
}

async function addTeamToEvent({ eventId, teamId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/AddTeamToEvent',
    data: sanitizePostData({ eventId, teamId }),
  });
  return data;
}

async function removeTeamFromEvent({ eventId, teamId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/RemoveTeamFromEvent',
    data: sanitizePostData({ eventId, teamId }),
  });
  return data;
}

async function addEventPost({ eventId, text }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/AddEventPost',
    data: sanitizePostData({ eventId, message: { text } }),
  });
  return data;
}

async function addEventPostComment({ eventPostId, text }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/AddEventPostComment',
    data: sanitizePostData({ eventPostId, message: { text } }),
    generalErrorsOnly: true,
  });
  return data;
}

async function updateEventEntry({ eventLogEntryId, text }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/UpdateEventEntry',
    data: sanitizePostData({ eventLogEntryId, message: { text } }),
    generalErrorsOnly: true,
  });
  return data;
}

async function removeEventEntry({ eventLogEntryId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/RemoveEventEntry',
    data: sanitizePostData({ eventLogEntryId }),
  });
  return data;
}

async function addEventSocialNetwork({
  eventId,
  accountLink,
  socialNetworkId,
}) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/AddEventSocialNetwork',
    data: sanitizePostData({ eventId, accountLink, socialNetworkId }),
    generalErrorsOnly: true,
  });
  return data;
}

async function updateEventSocialNetwork({ accountLink, eventSocialNetworkId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/UpdateEventSocialNetwork',
    data: sanitizePostData({ accountLink, eventSocialNetworkId }),
    generalErrorsOnly: true,
  });
  return data;
}

async function removeEventSocialNetwork({ eventSocialNetworkId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/RemoveEventSocialNetwork',
    data: sanitizePostData({ eventSocialNetworkId }),
  });
  return data;
}

async function sendMessageToEventAdmin({ eventId, text }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/SendMessageToEventAdmin',
    data: sanitizePostData({
      eventId,
      message: {
        text,
      },
    }),
    generalErrorsOnly: true,
  });
  return data;
}

async function searchEvent(searchFormData) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/UserEvent/SearchEvent',
    data: sanitizePostData(searchFormData),
    generalErrorsOnly: true,
  });
  return data;
}

async function getEventStatusToSearchDescriptions() {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/UserEvent/GetEventStatusToSearchDescriptions',
  });
  return data;
}

async function getUserActiveEvents() {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/UserEvent/GetUserActiveEvents',
  });
  return data;
}

async function getUserPastEvents() {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/UserEvent/GetUserPastEvents',
  });
  return data;
}

async function deleteParticipantFromEvent({ eventParticipantId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/DeleteParticipantFromEvent',
    data: sanitizePostData({ eventParticipantId }),
  });
  return data;
}

async function removeParticipantFunction({
  participantId,
  participantFunction,
}) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/RemoveParticipantFunction',
    data: sanitizePostData({ participantId, participantFunction }),
  });
  return data;
}

async function addParticipantFunction({ participantId, participantFunction }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/AddParticipantFunction',
    data: sanitizePostData({ participantId, participantFunction }),
  });
  return data;
}

async function cancelOutgoingInvitation({ invitationId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/CancelOutgoingInvitation',
    data: sanitizePostData({ invitationId }),
  });
  return data;
}

async function leaveEvent({ eventId }) {
  const { data } = await apiClient({
    method: 'post',
    url: '/api/Event/LeaveEvent',
    data: sanitizePostData({ eventId }),
  });
  return data;
}

async function getEventMessages({ eventId }) {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetEventMessages',
    params: { EventId: eventId },
  });
  return data;
}

async function getEventIdByLink({ link }) {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetEventIdByLink',
    params: { link },
  });
  return data;
}

async function getEventImage(eventId) {
  const { data, headers } = await apiClient({
    method: 'get',
    url: '/api/Event/GetEventPicture',
    params: { EventId: eventId },
    responseType: 'arraybuffer', // To handle binary data properly
  });

  // Extract content type from headers
  const contentType = headers['content-type'];

  // Create a Blob from the received data
  const blob = new Blob([data], { type: contentType });

  // Return the Blob or a URL for it (depending on usage)
  return blob;
}

async function getVacancies(eventId, openOnly, skip, limit) {
  const { data } = await apiClient({
    method: 'get',
    url: '/api/Event/GetEventVacancies',
    params: { eventId, openOnly, skip, limit },
  });
  return data;
}

export {
  getEventIdByLink,
  getEvent,
  searchEvent,
  searchParticipant,
  getEventStatusToSearchDescriptions,
  getAchievementFunctionDescriptions,
  getEventParticipants,
  getIncomingEventApplications,
  getOutgoingInvitations,
  getEventPosts,
  getActivityEventTypeDescriptions,
  getEventLevelTypeDescriptions,
  getEventRoleDescriptions,
  getEventRoleForApplicationDescriptions,
  getEventRoleForParticipantDescriptions,
  addEvent,
  updateEvent,
  publishEvent,
  cancelEvent,
  deleteEvent,
  inviteUsersToEvent,
  sendApplicationToEvent,
  changeParticipantRole,
  approveEventApplication,
  leaveEventApplicationOnHold,
  putEventApplicationToWaitList,
  rejectEventApplication,
  returnEventApplication,
  addTeamToEvent,
  removeTeamFromEvent,
  addEventPost,
  addEventPostComment,
  updateEventEntry,
  removeEventEntry,
  addEventSocialNetwork,
  updateEventSocialNetwork,
  removeEventSocialNetwork,
  sendMessageToEventAdmin,
  getUserActiveEvents,
  getUserPastEvents,
  deleteParticipantFromEvent,
  removeParticipantFunction,
  addParticipantFunction,
  cancelOutgoingInvitation,
  leaveEvent,
  getEventMessages,
  getEventImage,
  getVacancies,
};
